'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { AnimatePresence, motion } from 'framer-motion';
import { AppHeader } from '@/components/public/AppHeader';
import { ScanningState } from '@/components/public/ScanningState';
import { VerifiedState } from '@/components/public/VerifiedState';
import { RevisitState } from '@/components/public/RevisitState';
import { AlreadyRegisteredState } from '@/components/public/AlreadyRegisteredState';
import { InvalidState } from '@/components/public/InvalidState';
import { VaultCard } from '@/components/public/VaultCard';
import { VaultPreview } from '@/components/public/VaultPreview';
import { DocumentsCard } from '@/components/public/DocumentsCard';
import { ProductSlider } from '@/components/public/ProductSlider';
import { getVault, isInVault, addToVault, getVaultEntry } from '@/lib/vault';
import type { VaultEntry } from '@/lib/vault';

interface SerialLookupData {
  serial: string;
  productName?: string;
  variantName?: string;
  productionDate?: string;
  manufacturingLot?: string;
  productionLine?: string;
  qualityCheck?: boolean;
  batchName?: string;
  isScanned?: boolean;
}

type VerifyState =
  | { kind: 'idle' }
  | { kind: 'scanning'; code: string }
  | { kind: 'verified'; code: string; entry: VaultEntry; scanId?: string | null }
  | { kind: 'revisit'; code: string; entry: VaultEntry; scanId?: string | null }
  | { kind: 'already-registered'; code: string; entry: VaultEntry; scanCount: number; uniqueDevices: number }
  | { kind: 'invalid'; code: string }
  | { kind: 'preview'; code: string; entry: VaultEntry }
  | { kind: 'not-scanned'; code: string }
  | { kind: 'rescan-required'; code: string }
  | { kind: 'serial-scanning'; serial: string }
  | { kind: 'serial-lookup'; data: SerialLookupData }
  | { kind: 'serial-not-found'; serial: string }
  | { kind: 'error'; code: string; message: string };

interface VerifyResponse {
  status: string;
  serialNumber?: number | null;
  batch?: { name: string; id: string; createdAt?: string } | null;
  scanCount?: number;
  uniqueDevices?: number;
  isFirstScan?: boolean;
  isOwner?: boolean;
  scannedAt?: string;
  scanId?: string | null;
  productionDetails?: {
    productionDate?: string;
    manufacturingLot?: string;
    productionLine?: string;
    qualityCheck?: boolean;
    operator?: { name: string; employeeId: string; department: string };
    serialIndex?: number;
  } | null;
  useByDate?: string;
  ownership?: {
    assignmentType?: string;
    clientName?: string;
  } | null;
  client?: {
    name?: string;
  } | null;
}

function goHome() {
  window.location.hash = '';
}

export default function HomePage() {
  const t = useTranslations('public');
  const [state, setState] = useState<VerifyState>({ kind: 'idle' });
  const [vault, setVault] = useState<VaultEntry[]>([]);
  const [mounted, setMounted] = useState(false);

  const refreshVault = useCallback(() => {
    setVault(getVault());
  }, []);

  // Read hash on mount and on hashchange
  useEffect(() => {
    setMounted(true);
    refreshVault();

    const handleHash = () => {
      const hash = decodeURIComponent(window.location.hash.slice(1));
      if (!hash) {
        setState({ kind: 'idle' });
        return;
      }

      // Serial number manual entry — #serial=<n>
      if (hash.startsWith('serial=')) {
        const serial = hash.slice(7);
        if (serial) {
          verifySerial(serial);
          return;
        }
      }

      // Check if this code is in the vault → preview cached data (owner)
      const entry = getVaultEntry(hash);
      if (entry) {
        setState({ kind: 'preview', code: hash, entry });
        return;
      }

      // Check sessionStorage for a non-owner scan from this session
      const tempRaw = sessionStorage.getItem(`verify_temp_${hash}`);
      if (tempRaw) {
        try {
          const temp = JSON.parse(tempRaw) as { entry: VaultEntry; scanCount: number; uniqueDevices: number };
          setState({ kind: 'already-registered', code: hash, entry: temp.entry, scanCount: temp.scanCount, uniqueDevices: temp.uniqueDevices });
          return;
        } catch { /* ignore corrupt data */ }
      }

      // Check localStorage for lightweight "previously seen" marker
      try {
        const seenCodes: string[] = JSON.parse(localStorage.getItem('verify_seen_codes') || '[]');
        if (seenCodes.includes(hash)) {
          setState({ kind: 'rescan-required', code: hash });
          return;
        }
      } catch { /* ignore */ }

      // Truly never scanned on this device
      setState({ kind: 'not-scanned', code: hash });
    };

    handleHash();
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Called when user enters a serial number manually via #serial=<n> */
  const verifySerial = async (serial: string) => {
    setState({ kind: 'serial-scanning', serial });
    try {
      const res = await fetch(`/api/verify-serial/${encodeURIComponent(serial)}`);
      const data = await res.json();

      if (data.status === 'NOT_FOUND' || data.status === 'INVALID_FORMAT') {
        setState({ kind: 'serial-not-found', serial });
        return;
      }

      if (data.status !== 'SERIAL_VERIFIED') {
        setState({ kind: 'error', code: serial, message: data.message || 'Verification failed' });
        return;
      }

      setState({
        kind: 'serial-lookup',
        data: {
          serial,
          productName: data.pairingDetails?.product?.name,
          variantName: data.pairingDetails?.productVariant?.name,
          productionDate: data.productionDetails?.productionDate,
          manufacturingLot: data.productionDetails?.manufacturingLot,
          productionLine: data.productionDetails?.productionLine,
          qualityCheck: data.productionDetails?.qualityCheck,
          batchName: data.batch?.name,
          isScanned: data.isScanned,
        },
      });
    } catch {
      setState({ kind: 'error', code: serial, message: 'Network error' });
    }
  };

  /** Called only from /c/[code] redirect (actual QR scan) */
  const verify = async (code: string) => {
    setState({ kind: 'scanning', code });

    try {
      const minDelay = new Promise((r) => setTimeout(r, 1800));
      const [res] = await Promise.all([
        fetch(`/api/verify/${encodeURIComponent(code)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        }),
        minDelay,
      ]);

      const data: VerifyResponse = await res.json();

      if (data.status === 'NOT_FOUND') {
        setState({ kind: 'invalid', code });
        return;
      }

      if (data.status === 'ERROR' || !res.ok) {
        setState({ kind: 'error', code, message: 'Verification failed' });
        return;
      }

      if (data.status === 'PRE_PRODUCTION') {
        setState({ kind: 'error', code, message: 'Verification is temporarily disabled.' });
        return;
      }

      const alreadyInVault = isInVault(code);
      const scanCount = data.scanCount || 1;
      const uniqueDevices = data.uniqueDevices || 1;

      // Build vault entry from API data (manufacturing + ownership)
      const vaultData: Parameters<typeof addToVault>[0] = {
        code,
        serialNumber: data.serialNumber || null,
        firstSeenAt: alreadyInVault
          ? (getVaultEntry(code)?.firstSeenAt || new Date().toISOString())
          : new Date().toISOString(),
        lastSeenAt: new Date().toISOString(),
        scanCount,
        batchName: data.batch?.name,
        // The QR-verify API returns the ownership client (brand), not a product.
        // Until codes are properly paired to products, don't fabricate a model
        // name — leave it unset so the UI shows the neutral PRODUCT_FALLBACK.
        // (The client/brand is still preserved separately in `clientName`.)
        productName: undefined,
        productionDate: data.productionDetails?.productionDate,
        useByDate: data.useByDate,
        manufacturingLot: data.productionDetails?.manufacturingLot,
        productionLine: data.productionDetails?.productionLine,
        qualityCheck: data.productionDetails?.qualityCheck,
        operatorName: data.productionDetails?.operator?.name,
        operatorDepartment: data.productionDetails?.operator?.department,
        batchCreatedAt: data.batch?.createdAt,
        clientName: data.ownership?.clientName || data.client?.name,
        assignmentType: data.ownership?.assignmentType,
      };

      // This device owns the code if it's in the local vault OR the server
      // recognises it (durable device cookie) — the latter restores ownership
      // even after the browser wiped localStorage (iOS/ITP).
      const ownsCode = data.isFirstScan || alreadyInVault || !!data.isOwner;

      // Store to vault if this device owns the code (re-populates a wiped vault).
      if (ownsCode) {
        addToVault(vaultData);
        refreshVault();
      }

      // Build entry from vault if stored, otherwise from API data directly
      const savedEntry: VaultEntry = ownsCode
        ? getVaultEntry(code)!
        : {
            code,
            serialNumber: vaultData.serialNumber ?? null,
            firstSeenAt: vaultData.firstSeenAt,
            lastSeenAt: vaultData.lastSeenAt,
            scanCount,
            batchName: vaultData.batchName,
            productName: vaultData.productName,
            productionDate: vaultData.productionDate,
            useByDate: vaultData.useByDate,
            manufacturingLot: vaultData.manufacturingLot,
            productionLine: vaultData.productionLine,
            qualityCheck: vaultData.qualityCheck,
            operatorName: vaultData.operatorName,
            operatorDepartment: vaultData.operatorDepartment,
            batchCreatedAt: vaultData.batchCreatedAt,
            clientName: vaultData.clientName,
            assignmentType: vaultData.assignmentType,
          };

      if (data.isFirstScan) {
        setState({ kind: 'verified', code, entry: savedEntry, scanId: data.scanId });
      } else if (ownsCode) {
        // Owner returning (vault OR durable device cookie recognised them).
        setState({ kind: 'revisit', code, entry: savedEntry, scanId: data.scanId });
      } else {
        // A different device registered this code — stash in sessionStorage for refresh support
        sessionStorage.setItem(`verify_temp_${code}`, JSON.stringify({ entry: savedEntry, scanCount, uniqueDevices }));
        // Lightweight localStorage marker so we recognise this code across tabs/sessions
        try {
          const seenCodes: string[] = JSON.parse(localStorage.getItem('verify_seen_codes') || '[]');
          if (!seenCodes.includes(code)) {
            seenCodes.push(code);
            localStorage.setItem('verify_seen_codes', JSON.stringify(seenCodes));
          }
        } catch { /* ignore */ }
        setState({ kind: 'already-registered', code, entry: savedEntry, scanCount, uniqueDevices });
      }
    } catch {
      setState({ kind: 'error', code, message: 'Network error' });
    }
  };

  // The /c/[code] route sets a flag so we know to actually call the API
  useEffect(() => {
    if (!mounted) return;
    const handler = () => {
      const hash = decodeURIComponent(window.location.hash.slice(1));
      if (!hash) return;

      // Check for the scan trigger flag
      const shouldScan = sessionStorage.getItem('verify_scan_trigger');
      if (shouldScan === hash) {
        sessionStorage.removeItem('verify_scan_trigger');
        verify(hash);
      }
    };
    handler();
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  if (!mounted) return null;

  const showingDetail = state.kind !== 'idle';

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'var(--pub-bg)' }}
    >
      <AppHeader />

      {/* Back button — shown on any non-idle state */}
      {showingDetail && (
        <div className="px-4 pb-2">
          <button
            onClick={goHome}
            className="flex items-center gap-1.5 transition-colors duration-150"
            style={{
              fontFamily: 'var(--font-pub-mono)',
              fontSize: 11,
              color: 'var(--pub-text-muted)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 0',
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {t('nav.back')}
          </button>
        </div>
      )}

      {/* Scroll area */}
      <div className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait">
          {state.kind === 'idle' && (
            <motion.div
              key="idle"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <HomeView vault={vault} />
            </motion.div>
          )}

          {state.kind === 'scanning' && (
            <motion.div
              key="scanning"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.3 }}
            >
              <ScanningState code={state.code} />
            </motion.div>
          )}

          {state.kind === 'verified' && (
            <motion.div
              key="verified"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.4 }}
            >
              <VerifiedState
                code={state.code}
                entry={state.entry}
                scanId={state.scanId}
              />
            </motion.div>
          )}

          {state.kind === 'revisit' && (
            <motion.div
              key="revisit"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.4 }}
            >
              <RevisitState
                code={state.code}
                entry={state.entry}
                scanId={state.scanId}
              />
            </motion.div>
          )}

          {state.kind === 'already-registered' && (
            <motion.div
              key="already-registered"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.4 }}
            >
              <AlreadyRegisteredState
                code={state.code}
                entry={state.entry}
                scanCount={state.scanCount}
                uniqueDevices={state.uniqueDevices}
              />
            </motion.div>
          )}

          {state.kind === 'invalid' && (
            <motion.div
              key="invalid"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.4 }}
            >
              <InvalidState code={state.code} />
            </motion.div>
          )}

          {state.kind === 'preview' && (
            <motion.div
              key="preview"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <VaultPreview entry={state.entry} />
            </motion.div>
          )}

          {state.kind === 'not-scanned' && (
            <motion.div
              key="not-scanned"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <NotScannedState code={state.code} />
            </motion.div>
          )}

          {state.kind === 'rescan-required' && (
            <motion.div
              key="rescan-required"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <RescanRequiredState code={state.code} />
            </motion.div>
          )}

          {(state.kind === 'serial-scanning' || state.kind === 'serial-lookup' || state.kind === 'serial-not-found') && (
            <motion.div
              key="serial"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {state.kind === 'serial-scanning' && <SerialScanningState serial={state.serial} />}
              {state.kind === 'serial-lookup' && <SerialLookupState data={state.data} />}
              {state.kind === 'serial-not-found' && <SerialNotFoundState serial={state.serial} />}
            </motion.div>
          )}

          {state.kind === 'error' && (
            <motion.div
              key="error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex flex-col items-center gap-4 px-4 pt-12" style={{ paddingBottom: 100 }}>
                <p style={{ color: 'var(--pub-text-secondary)', fontSize: 14 }}>{state.message}</p>
                <button
                  className="pub-btn pub-btn-primary"
                  style={{ maxWidth: 200 }}
                  onClick={goHome}
                >
                  {t('invalid.scanAgain')}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/** "Not scanned yet" — shown when someone visits #code manually and it's not in vault */
function NotScannedState({ code }: { code: string }) {
  const t = useTranslations('public');

  return (
    <div className="flex flex-col gap-3 px-4" style={{ paddingBottom: 100 }}>
      <div
        className="pub-card flex flex-col items-center text-center gap-4"
        style={{ padding: '32px 20px' }}
      >
        {/* Icon */}
        <div
          className="flex items-center justify-center"
          style={{
            width: 56,
            height: 56,
            borderRadius: 16,
            background: 'var(--pub-surface-3)',
            border: '1px solid var(--pub-border)',
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="var(--pub-text-muted)" strokeWidth="2" width="24" height="24">
            <path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2" />
            <rect x="7" y="7" width="10" height="10" rx="1" />
          </svg>
        </div>

        <h1
          style={{
            fontFamily: 'var(--font-pub-display)',
            fontSize: 20,
            fontWeight: 700,
            color: 'var(--pub-text-primary)',
          }}
        >
          {t('notScanned.title')}
        </h1>

        {/* Code pill */}
        <span
          style={{
            fontFamily: 'var(--font-pub-mono)',
            fontSize: 12,
            color: 'var(--pub-text-secondary)',
            background: 'var(--pub-surface-3)',
            border: '1px solid var(--pub-border)',
            padding: '5px 16px',
            borderRadius: 100,
            letterSpacing: '0.1em',
          }}
        >
          {code}
        </span>

        <p
          style={{
            fontSize: 13,
            color: 'var(--pub-text-secondary)',
            lineHeight: 1.6,
            maxWidth: 300,
          }}
        >
          {t('notScanned.body')}
        </p>
      </div>

      <button className="pub-btn pub-btn-primary" onClick={goHome}>
        {t('nav.back')}
      </button>
    </div>
  );
}

/** "Rescan required" — shown when a non-owner revisits #code in a new tab/session */
function RescanRequiredState({ code }: { code: string }) {
  const t = useTranslations('public');

  return (
    <div className="flex flex-col gap-3 px-4" style={{ paddingBottom: 100 }}>
      <div
        className="pub-card flex flex-col items-center text-center gap-4"
        style={{ padding: '32px 20px' }}
      >
        {/* QR icon with refresh indicator */}
        <div
          className="flex items-center justify-center"
          style={{
            width: 56,
            height: 56,
            borderRadius: 16,
            background: 'rgba(245, 158, 11, 0.08)',
            border: '1px solid rgba(245, 158, 11, 0.2)',
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="rgb(245, 158, 11)" strokeWidth="2" width="24" height="24">
            <path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2" />
            <path d="M8 12h8M12 8v8" />
          </svg>
        </div>

        <h1
          style={{
            fontFamily: 'var(--font-pub-display)',
            fontSize: 20,
            fontWeight: 700,
            color: 'var(--pub-text-primary)',
          }}
        >
          {t('rescanRequired.title')}
        </h1>

        {/* Code pill */}
        <span
          style={{
            fontFamily: 'var(--font-pub-mono)',
            fontSize: 12,
            color: 'var(--pub-text-secondary)',
            background: 'var(--pub-surface-3)',
            border: '1px solid var(--pub-border)',
            padding: '5px 16px',
            borderRadius: 100,
            letterSpacing: '0.1em',
          }}
        >
          {code}
        </span>

        <p
          style={{
            fontSize: 13,
            color: 'var(--pub-text-secondary)',
            lineHeight: 1.6,
            maxWidth: 300,
          }}
        >
          {t('rescanRequired.body')}
        </p>
      </div>

      <button className="pub-btn pub-btn-primary" onClick={goHome}>
        {t('nav.back')}
      </button>
    </div>
  );
}

/** Home / Vault view — shown when no code is in the URL hash */
function HomeView({ vault }: { vault: VaultEntry[] }) {
  const t = useTranslations('public');

  return (
    <div className="flex flex-col gap-3 px-4" style={{ paddingBottom: 100 }}>
      {/* Hero card */}
      <div
        className="pub-card"
        style={{
          padding: 20,
          background: 'linear-gradient(155deg, rgba(34,197,94,0.05) 0%, transparent 55%)',
        }}
      >
        <h1
          style={{
            fontFamily: 'var(--font-pub-display)',
            fontSize: 26,
            fontWeight: 800,
            letterSpacing: '-0.03em',
            color: 'var(--pub-text-primary)',
            marginBottom: 6,
            lineHeight: 1.1,
          }}
        >
          {t('home.title')}
        </h1>
        <p
          style={{
            fontSize: 13,
            color: 'var(--pub-text-secondary)',
            lineHeight: 1.55,
            marginBottom: 16,
          }}
        >
          {t('home.subtitle')}
        </p>

        {/* Scan CTA */}
        <div
          className="flex items-center gap-3 cursor-pointer transition-colors duration-200"
          style={{
            background: 'var(--pub-surface-3)',
            border: '1px dashed rgba(34,197,94,0.3)',
            borderRadius: 14,
            padding: 14,
          }}
        >
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: 'var(--pub-green-dim)',
              border: '1px solid rgba(34,197,94,0.2)',
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="var(--pub-green)" strokeWidth="2" width="18" height="18">
              <path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2" />
              <rect x="7" y="7" width="10" height="10" rx="1" />
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--pub-text-primary)' }}>
              {t('home.scanCta')}
            </div>
            <div style={{ fontSize: 11, color: 'var(--pub-text-muted)', marginTop: 2 }}>
              {t('home.scanCtaSub')}
            </div>
          </div>
        </div>

        {/* Serial number fallback — always visible, no toggle */}
        <SerialEntryInline />
      </div>

      {/* Vault card — compact, shows 2 items max with expand */}
      <VaultCard entries={vault} />

      {/* Document Access */}
      <DocumentsCard />

      {/* Product slider */}
      <ProductSlider />

      {/* AAS Approved brand card */}
      <div
        className="pub-card flex items-center gap-3"
        style={{ padding: '14px 16px' }}
      >
        <div
          className="flex items-center justify-center flex-shrink-0"
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            background: 'var(--pub-surface-3)',
            border: '1px solid var(--pub-border)',
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="var(--pub-green)" strokeWidth="2" width="18" height="18">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
        </div>
        <div className="min-w-0">
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--pub-text-primary)' }}>
            {t('home.aasTitle')}
          </div>
          <div style={{ fontSize: 11, color: 'var(--pub-text-muted)', marginTop: 2, lineHeight: 1.4 }}>
            {t('home.aasSub')}
          </div>
        </div>
      </div>

      {/* Website link card */}
      <a
        href="https://absoluteeclipse.eu"
        target="_blank"
        rel="noopener noreferrer"
        className="pub-card flex items-center gap-3 transition-colors duration-200"
        style={{ padding: '14px 16px', textDecoration: 'none' }}
      >
        <div
          className="flex items-center justify-center flex-shrink-0"
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            background: 'var(--pub-surface-3)',
            border: '1px solid var(--pub-border)',
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="var(--pub-text-secondary)" strokeWidth="2" width="18" height="18">
            <circle cx="12" cy="12" r="10" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--pub-text-primary)' }}>
            {t('home.websiteTitle')}
          </div>
          <div style={{ fontSize: 11, color: 'var(--pub-text-muted)', marginTop: 2 }}>
            absoluteeclipse.eu
          </div>
        </div>
        <svg viewBox="0 0 24 24" fill="none" stroke="var(--pub-text-muted)" strokeWidth="2" width="14" height="14" style={{ flexShrink: 0 }}>
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </a>

      {/* Contact card */}
      <a
        href="/contact"
        className="pub-card flex items-center gap-3 transition-colors duration-200"
        style={{ padding: '14px 16px', textDecoration: 'none' }}
      >
        <div
          className="flex items-center justify-center flex-shrink-0"
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            background: 'var(--pub-surface-3)',
            border: '1px solid var(--pub-border)',
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="var(--pub-text-secondary)" strokeWidth="2" width="18" height="18">
            <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--pub-text-primary)' }}>
            {t('home.contactTitle')}
          </div>
          <div style={{ fontSize: 11, color: 'var(--pub-text-muted)', marginTop: 2 }}>
            {t('home.contactSub')}
          </div>
        </div>
        <svg viewBox="0 0 24 24" fill="none" stroke="var(--pub-text-muted)" strokeWidth="2" width="14" height="14" style={{ flexShrink: 0 }}>
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </a>
    </div>
  );
}

/** Serial entry — plain number field (serials are not zero-padded and vary in length) */
function SerialEntryInline() {
  const t = useTranslations('public');
  const [value, setValue] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Serials on the sticker are NOT zero-padded and vary in length (1–7 digits),
  // so this is a plain number field — fixed digit boxes wrongly implied that
  // exactly 7 digits were required. Cap at 7 (current serial space tops out
  // around 3.4M); the lookup 404s on anything unknown anyway.
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 7);
    setValue(raw);
  };

  const submit = () => {
    if (!value) return;
    window.location.hash = `serial=${parseInt(value, 10)}`;
  };

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1, height: 1, background: 'var(--pub-border)' }} />
        <span style={{ fontSize: 10, color: 'var(--pub-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>
          {t('serial.orEnter')}
        </span>
        <div style={{ flex: 1, height: 1, background: 'var(--pub-border)' }} />
      </div>

      {/* What to type and where to find it */}
      <p
        style={{
          fontSize: 11.5,
          color: 'var(--pub-text-muted)',
          textAlign: 'center',
          margin: '0 0 8px',
          lineHeight: 1.45,
        }}
      >
        {t('serial.helper')}
      </p>

      {/* Plain number field — any length, exactly as printed on the sticker */}
      <div
        onClick={() => inputRef.current?.focus()}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          height: 48,
          padding: '0 14px',
          borderRadius: 12,
          cursor: 'text',
          background: 'var(--pub-surface-3)',
          border: `${focused ? 2 : 1}px solid ${focused ? 'var(--pub-green)' : 'var(--pub-border)'}`,
          transition: 'border-color 0.15s',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-pub-mono)',
            fontSize: 18,
            fontWeight: 700,
            color: value ? 'var(--pub-green)' : 'var(--pub-text-muted)',
          }}
        >
          #
        </span>
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={value}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={e => e.key === 'Enter' && submit()}
          placeholder={t('serial.placeholder')}
          aria-label={t('serial.ariaLabel')}
          style={{
            flex: 1,
            width: '100%',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontFamily: 'var(--font-pub-mono)',
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: '0.06em',
            color: 'var(--pub-text-primary)',
          }}
        />
      </div>

      {/* Submit — only visible once something is typed */}
      {value.length > 0 && (
        <button
          onClick={submit}
          className="pub-btn pub-btn-primary"
          style={{ width: '100%', marginTop: 8, fontSize: 13 }}
        >
          {t('serial.check', { number: parseInt(value, 10).toLocaleString() })}
        </button>
      )}
    </div>
  );
}

/** Loading state while serial API call is in flight */
function SerialScanningState({ serial }: { serial: string }) {
  const t = useTranslations('public');

  return (
    <div className="flex flex-col gap-3 px-4" style={{ paddingBottom: 100 }}>
      <div className="pub-card flex flex-col items-center text-center gap-4" style={{ padding: '32px 20px' }}>
        <div
          className="flex items-center justify-center"
          style={{ width: 56, height: 56, borderRadius: 16, background: 'var(--pub-surface-3)', border: '1px solid var(--pub-border)' }}
        >
          <svg className="animate-spin" viewBox="0 0 24 24" fill="none" stroke="var(--pub-text-muted)" strokeWidth="2" width="24" height="24">
            <path d="M21 12a9 9 0 11-6.219-8.56" />
          </svg>
        </div>
        <p style={{ fontSize: 13, color: 'var(--pub-text-secondary)' }}>{t('serial.scanning', { serial })}</p>
      </div>
    </div>
  );
}

/** Result state for serial number lookup */
function SerialLookupState({ data }: { data: SerialLookupData }) {
  const t = useTranslations('public');
  const formatDate = (s: string) =>
    new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="flex flex-col gap-3 px-4" style={{ paddingBottom: 100 }}>

      {/* Status card */}
      <div className="pub-card flex flex-col gap-4" style={{ padding: '20px' }}>
        <div className="flex items-center gap-3">
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{ width: 46, height: 46, borderRadius: 14, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)' }}
          >
            {/* Shield + check — a found record is good news, not a warning */}
            <svg viewBox="0 0 24 24" fill="none" stroke="rgb(59,130,246)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <polyline points="9 12 11 14 15 10" />
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--pub-text-primary)' }}>{t('serial.verified.title')}</div>
            <div style={{ fontSize: 13, fontFamily: 'var(--font-pub-mono)', color: 'rgb(59,130,246)', marginTop: 2 }}>#{data.serial}</div>
          </div>
        </div>

        {/* Confirmation — informational, deliberately not a warning */}
        <div
          style={{
            background: 'rgba(59,130,246,0.06)',
            border: '1px solid rgba(59,130,246,0.2)',
            borderRadius: 12,
            padding: '12px 14px',
            display: 'flex',
            gap: 10,
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="rgb(59,130,246)" strokeWidth="2" width="18" height="18" style={{ flexShrink: 0, marginTop: 1 }}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'rgb(59,130,246)' }}>
              {t('serial.verified.confirmedTitle')}
            </div>
            <p style={{ fontSize: 12.5, color: 'var(--pub-text-secondary)', lineHeight: 1.55, margin: '4px 0 0' }}>
              {t('serial.verified.confirmedDesc')}
            </p>
            <p style={{ fontSize: 12.5, color: 'var(--pub-text-secondary)', lineHeight: 1.55, margin: '6px 0 0' }}>
              {t('serial.verified.scanQrHint')}
            </p>
          </div>
        </div>

        {/* Production details */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {data.productName && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--pub-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>{t('serial.verified.product')}</div>
              <div style={{ fontSize: 13, color: 'var(--pub-text-primary)' }}>{data.productName}{data.variantName ? ` · ${data.variantName}` : ''}</div>
            </div>
          )}
          {data.productionDate && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--pub-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>{t('serial.verified.productionDate')}</div>
              <div style={{ fontSize: 13, color: 'var(--pub-text-primary)' }}>{formatDate(data.productionDate)}</div>
            </div>
          )}
          {data.manufacturingLot && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--pub-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>{t('serial.verified.lot')}</div>
              <div style={{ fontSize: 13, fontFamily: 'var(--font-pub-mono)', color: 'var(--pub-text-primary)' }}>{data.manufacturingLot}</div>
            </div>
          )}
          {data.productionLine && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--pub-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>{t('serial.verified.line')}</div>
              <div style={{ fontSize: 13, color: 'var(--pub-text-primary)' }}>{data.productionLine}</div>
            </div>
          )}
          {data.batchName && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--pub-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>{t('serial.verified.batch')}</div>
              <div style={{ fontSize: 13, color: 'var(--pub-text-primary)' }}>{data.batchName}</div>
            </div>
          )}
          {data.qualityCheck !== undefined && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--pub-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>{t('serial.verified.qc')}</div>
              <div style={{ fontSize: 13, color: data.qualityCheck ? 'var(--pub-green)' : 'rgb(239,68,68)' }}>{data.qualityCheck ? t('serial.verified.passed') : t('serial.verified.failed')}</div>
            </div>
          )}
        </div>
      </div>

      {/* Primary next step — scan the QR for the full authenticity + ownership check */}
      <button
        onClick={goHome}
        className="pub-card flex items-center gap-3 text-left"
        style={{
          padding: '14px 16px',
          background: 'var(--pub-text-primary)',
          borderColor: 'var(--pub-text-primary)',
          cursor: 'pointer',
          width: '100%',
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="var(--pub-bg)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="26" height="26" style={{ flexShrink: 0 }}>
          <path d="M3 7V5a2 2 0 0 1 2-2h2" />
          <path d="M17 3h2a2 2 0 0 1 2 2v2" />
          <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
          <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
          <rect x="7" y="7" width="4" height="4" />
          <rect x="13" y="13" width="4" height="4" />
        </svg>
        <span>
          <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: 'var(--pub-bg)' }}>
            {t('serial.verified.scanQr')}
          </span>
          <span style={{ display: 'block', fontSize: 11.5, color: 'var(--pub-bg)', opacity: 0.75, marginTop: 2, lineHeight: 1.45 }}>
            {t('serial.verified.scanQrSub')}
          </span>
        </span>
      </button>

      <button className="pub-btn" onClick={goHome} style={{ background: 'var(--pub-surface-3)', color: 'var(--pub-text-secondary)' }}>
        {t('nav.back')}
      </button>
    </div>
  );
}

/** Serial not found */
function SerialNotFoundState({ serial }: { serial: string }) {
  const t = useTranslations('public');

  return (
    <div className="flex flex-col gap-3 px-4" style={{ paddingBottom: 100 }}>
      <div className="pub-card flex flex-col items-center text-center gap-4" style={{ padding: '32px 20px' }}>
        <div
          className="flex items-center justify-center"
          style={{ width: 56, height: 56, borderRadius: 16, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="rgb(239,68,68)" strokeWidth="2" width="24" height="24">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        </div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--pub-text-primary)', marginBottom: 6 }}>{t('serial.notFound.title')}</div>
          <div style={{ fontSize: 12, fontFamily: 'var(--font-pub-mono)', color: 'var(--pub-text-muted)', marginBottom: 10 }}>#{serial}</div>
          <p style={{ fontSize: 13, color: 'var(--pub-text-secondary)', lineHeight: 1.55, maxWidth: 280 }}>
            {t('serial.notFound.body')}
          </p>
        </div>
      </div>
      <button className="pub-btn pub-btn-primary" onClick={goHome}>
        {t('serial.notFound.tryAgain')}
      </button>
    </div>
  );
}
